// file: SubscribeUserModal.tsx
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Modal,
  Button,
  Checkbox,
  Space,
  message,
  Icon,
  Paragraph,
  Title,
  Row,
  Form,
  Select,
  SelectOption,
} from 'components';
import { capitalize, featureEnabled } from 'utils';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import TextArea from 'antd/es/input/TextArea';
import {
  createSupraSystemSubscription,
  sendSupraSystemSubscriptionInvitation,
} from 'api/SupraSystemSubscriptionController';
import { isUserFriendlyError } from 'api/SupraSystemSubscriptionController/sendSupraSystemSubscriptionInvitation';
import { SupraSystemSubscriptionStatus } from '@bb/domain/dist/types';
import styled from 'styled-components';
import { UserRoles, UserSubRoles } from '@bb/users';
import { AuthContext } from 'contexts';
import { SupraSystemSubscriptionType } from '@bb/domain/dist/types/SupraSystemSubscription';
import checkAutomaticActivationFeeWaive from 'api/SupraSystemSubscriptionController/checkAutomaticActivationFeeWaive';
import { ERROR_MESSAGES } from '../../UserViewPage/constants';
import { UserState } from '../../UserViewPage/types';
import { useActivationFeeContext } from '../ActivationFeeContext';

type SupraInviteModalProps = {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
  user: {
    id: string;
    orgId: string;
    email: string;
    supraSubscriptionStatus: SupraSystemSubscriptionStatus;
    role: UserRoles;
  } | null;
  setLoading: (loading: boolean) => void;
  activationRate?: string;
  loading: boolean;
  canCreateNonBillableKeys?: boolean;
};

type FieldsDirty = {
  keyType: boolean;
  subRole: boolean;
  waiveActivationFeeReason: boolean;
};

/**
 * This component handles both subscribing the user and sending the Supra invite.
 * It has local state for "waiveFee" and "subscribeWithoutFeeReason,"
 */
const SubscribeUserModal: React.FC<SupraInviteModalProps> = ({
  visible,
  onClose,
  onSuccess,
  user,
  setLoading,
  activationRate,
  loading,
  canCreateNonBillableKeys = true,
}) => {
  const { auth, orgSupraSubscriptionOptions } = useContext(AuthContext);

  const [
    isActivationFeeAutomaticallyWaived,
    setIsActivationFeeAutomaticallyWaived,
  ] = useState<boolean>(false);
  const [waiveFee, setWaiveFee] = useState<boolean>(
    isActivationFeeAutomaticallyWaived,
  );
  const [subscribeWithoutFeeReason, setSubscribeWithoutFeeReason] =
    useState('');
  const [selectedKeyType, setSelectedKeyType] =
    useState<SupraSystemSubscriptionType>();
  const [selectedSubRole, setSelectedSubRole] = useState<UserSubRoles>();
  const [billableStatus, setBillableStatus] = useState<
    'billable' | 'non-billable' | ''
  >('');
  const [fieldsAreDirty, setFieldsAreDirty] = useState<FieldsDirty>({
    keyType: false,
    subRole: false,
    waiveActivationFeeReason: false,
  });

  const handleClose = () => {
    setSelectedKeyType(undefined);
    setBillableStatus('');
    setSelectedSubRole(undefined);
    setWaiveFee(false);
    setSubscribeWithoutFeeReason('');
    setFieldsAreDirty({
      keyType: false,
      subRole: false,
      waiveActivationFeeReason: false,
    });
    reviewAutomaticActivationFeeWaive();
    onClose();
  };

  const { canWaiveActivationFee } = useActivationFeeContext();

  const isTrainingKeyLimitExceeded = useMemo(
    () =>
      !canCreateNonBillableKeys && selectedSubRole === UserSubRoles.Training,
    [canCreateNonBillableKeys, selectedSubRole],
  );

  const supraSubscriptionType = useMemo(() => {
    if (featureEnabled('supraBillingApiFeature') && user) {
      const { keyTypes: orgKeyTypeOptions } = orgSupraSubscriptionOptions || {};
      // Note: added check for FF 'decoupleKeyTypeFeature' to show all types regardless of role
      if (featureEnabled('decoupleKeyTypeFeature')) {
        return orgKeyTypeOptions || [];
      }
      if (user.role === UserRoles.Agent) {
        return [
          ...(orgKeyTypeOptions?.includes(SupraSystemSubscriptionType.FULL)
            ? [SupraSystemSubscriptionType.FULL]
            : []),
        ];
      }
      if (user.role === UserRoles['Non-Member']) {
        return [
          ...(orgKeyTypeOptions?.includes(SupraSystemSubscriptionType.LIMITED)
            ? [SupraSystemSubscriptionType.LIMITED]
            : []),
          ...(orgKeyTypeOptions?.includes(
            SupraSystemSubscriptionType.MANAGED_ACCESS,
          )
            ? [SupraSystemSubscriptionType.MANAGED_ACCESS]
            : []),
        ];
      }
      return orgKeyTypeOptions || [];
    }
    // Note: added check for FF 'decoupleKeyTypeFeature' to show all types regardless of role
    if (user && featureEnabled('decoupleKeyTypeFeature')) {
      return [
        SupraSystemSubscriptionType.FULL,
        SupraSystemSubscriptionType.LIMITED,
        SupraSystemSubscriptionType.MANAGED_ACCESS,
      ];
    }

    if (user && user.role === UserRoles.Agent) {
      return [SupraSystemSubscriptionType.FULL];
    }
    if (user && user.role === UserRoles['Non-Member']) {
      return [
        SupraSystemSubscriptionType.LIMITED,
        SupraSystemSubscriptionType.MANAGED_ACCESS,
      ];
    }

    return [
      SupraSystemSubscriptionType.FULL,
      SupraSystemSubscriptionType.LIMITED,
      SupraSystemSubscriptionType.MANAGED_ACCESS,
    ];
  }, [user, orgSupraSubscriptionOptions]);

  const supraSubKeyTypes = useMemo(() => {
    if (featureEnabled('supraBillingApiFeature')) {
      const { limitedSubRoles, managedAccessSubRoles } =
        orgSupraSubscriptionOptions || {};

      if (selectedKeyType === SupraSystemSubscriptionType.LIMITED) {
        return limitedSubRoles;
      }
      if (selectedKeyType === SupraSystemSubscriptionType.MANAGED_ACCESS) {
        return managedAccessSubRoles;
      }
      return [];
    }

    if (selectedKeyType === SupraSystemSubscriptionType.LIMITED) {
      return [
        UserSubRoles.Affiliate,
        UserSubRoles.Training,
        UserSubRoles['SupraNET Manual Coop'],
      ];
    }
    if (selectedKeyType === SupraSystemSubscriptionType.MANAGED_ACCESS) {
      return [
        UserSubRoles['Non-Member Agent'],
        UserSubRoles['Inspector/Appraiser'],
        UserSubRoles['Pest Control'],
        UserSubRoles['Photographer/Stager'],
        UserSubRoles.Tradesperson,
        UserSubRoles.Other,
      ];
    }
    return [];
  }, [orgSupraSubscriptionOptions, selectedKeyType]);

  const supraSubscriptionTypeValid = useMemo(
    () => !!selectedKeyType,
    [selectedKeyType],
  );

  // If the subscription type exists and is LIMITED or MANAGED ACCESS, check whether the Sub Key type is valid. Otherwise, it always is.
  const userSubRoleValid = useMemo(
    () =>
      selectedKeyType &&
      [
        SupraSystemSubscriptionType.LIMITED,
        SupraSystemSubscriptionType.MANAGED_ACCESS,
      ].includes(selectedKeyType)
        ? !!selectedSubRole
        : true,
    [selectedKeyType, selectedSubRole],
  );

  const keyTypeHelpMessage = useMemo(() => {
    if (
      user &&
      user.role === UserRoles['Non-Member'] &&
      supraSubscriptionType.length === 0 &&
      !featureEnabled('decoupleKeyTypeFeature')
    ) {
      return ERROR_MESSAGES.nonMemberNoKeyTypes;
    }
    if (!supraSubscriptionTypeValid && fieldsAreDirty.keyType) {
      return ERROR_MESSAGES.supraSubscriptionKeyTypeRequired;
    }
    return undefined;
  }, [
    fieldsAreDirty.keyType,
    supraSubscriptionType.length,
    supraSubscriptionTypeValid,
    user,
  ]);

  const subRoleHelpMessage = useMemo(() => {
    if (!userSubRoleValid && fieldsAreDirty.subRole) {
      return ERROR_MESSAGES.supraSubscriptionSubRoleRequired;
    }
    return undefined;
  }, [fieldsAreDirty.subRole, userSubRoleValid]);

  const handleKeyTypeChange = (type: SupraSystemSubscriptionType) => {
    if (
      [
        SupraSystemSubscriptionType.FULL,
        SupraSystemSubscriptionType.MANAGED_ACCESS,
      ].includes(type)
    ) {
      setSelectedKeyType(type);
      setBillableStatus('billable');
      setSelectedSubRole(null);
    } else {
      setSelectedKeyType(type);
      setBillableStatus('');
      setSelectedSubRole(null);
    }
    setFieldsAreDirty(cur => ({ ...cur, keyType: true, subRole: true }));
  };

  const handleSubKeyTypeChange = (subRole: UserSubRoles) => {
    if (subRole === UserSubRoles.Affiliate) {
      setBillableStatus('billable');
      setSelectedSubRole(subRole);
    } else if (
      subRole === UserSubRoles.Training ||
      subRole === UserSubRoles['SupraNET Manual Coop']
    ) {
      setBillableStatus('non-billable');
      setSelectedSubRole(subRole);
    } else {
      let billing: Required<UserState>['supraSubscription']['billing'] = '';
      if (selectedKeyType === SupraSystemSubscriptionType.MANAGED_ACCESS) {
        billing = 'billable';
      } else {
        billing =
          orgSupraSubscriptionOptions?.limitedOtherBilling?.length === 1
            ? orgSupraSubscriptionOptions.limitedOtherBilling[0]
            : '';
      }
      setBillableStatus(billing);
      setSelectedSubRole(subRole);
    }
    setFieldsAreDirty(cur => ({ ...cur, keyType: true, subRole: true }));
  };

  const handleWaiveActivationFee = (isChecked: boolean) => {
    setWaiveFee(isChecked);

    // Clear the reason when unchecking
    if (!isChecked) {
      setSubscribeWithoutFeeReason('');
    }

    setFieldsAreDirty(cur => ({
      ...cur,
      waiveActivationFeeReason: true,
    }));
  };

  const onSendInvite = async () => {
    if (!user) return;

    // If user wants to waive fee but hasn't entered a reason, show a warning
    if (
      featureEnabled('keyholderBillingFeature') &&
      waiveFee &&
      !subscribeWithoutFeeReason.trim()
    ) {
      message.warning('Please enter a reason before submitting.');
      return;
    }

    try {
      setLoading(true);

      // Create subscription if user is unsubscribed
      if (
        user.supraSubscriptionStatus ===
        SupraSystemSubscriptionStatus.unsubscribed
      ) {
        let billing;
        if (billableStatus) {
          billing = billableStatus === 'billable';
        }
        await createSupraSystemSubscription({
          orgId: user.orgId,
          userId: user.id,
          type: selectedKeyType,
          subRole: selectedSubRole,
          ...(billing !== undefined && { billing }),
          waiveActivationFee: waiveFee,
          waiveActivationFeeReason: waiveFee
            ? subscribeWithoutFeeReason.trim()
            : undefined,
        });
        message.success('Supra subscription created');
      }

      // Send the invite
      await sendSupraSystemSubscriptionInvitation({
        orgId: user.orgId,
        userId: user.id,
      });
      message.success('Supra subscription invitation sent');

      setLoading(false);
      handleClose();

      // handle side effects last to avoid re-rendering issues
      await onSuccess();
    } catch (e: any) {
      const errorMsg = isUserFriendlyError(e?.response?.data?.message)
        ? e.response.data.message
        : 'Something went wrong, please try again.';
      message.error(errorMsg);
      setLoading(false);
      handleClose();
    }
  };

  // If a user was passed in who will be the one auth is manipulating,
  // check their history
  const reviewAutomaticActivationFeeWaive = useCallback(async () => {
    if (!user || !auth.isAdmin) return;
    const automaticallyWaiveActivationFee =
      await checkAutomaticActivationFeeWaive({
        userId: user.id,
      });

    // A user who has never had a Supra One subscription before, but had
    // legacy eKey data should always have their Activation Fee waived.
    if (automaticallyWaiveActivationFee) {
      setIsActivationFeeAutomaticallyWaived(true);
      setWaiveFee(true);
      setSubscribeWithoutFeeReason(
        'Starting brand-new Supra One subscription for user with legacy eKey data',
      );
    }
  }, [auth.isAdmin, user]);

  useEffect(() => {
    reviewAutomaticActivationFeeWaive();
  }, [reviewAutomaticActivationFeeWaive]);

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      title="Send Supra system invitation"
      footer={null}
      destroyOnClose
    >
      <Paragraph>
        This will send an invite to{' '}
        {user ? `user ${user.email}` : 'unknown user'} where they will need to
        activate their subscription.
      </Paragraph>

      {featureEnabled('keyholderBillingFeature') && (
        <>
          <StyledTitle level={4}>Subscription</StyledTitle>
          <Row>
            <Form.Item
              style={{ minWidth: '100%' }}
              label="Key Type"
              validateStatus={
                !supraSubscriptionTypeValid && fieldsAreDirty.keyType
                  ? 'error'
                  : undefined
              }
              help={keyTypeHelpMessage}
            >
              <Select
                data-cy="user-select-supra-keytype"
                showSearch
                placeholder="Select"
                optionFilterProp="children"
                value={selectedKeyType}
                onChange={handleKeyTypeChange}
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
                onBlur={() =>
                  setFieldsAreDirty(cur => ({
                    ...cur,
                    keyType: true,
                    subRole: true,
                  }))
                }
                style={{ textTransform: 'capitalize' }}
              >
                {supraSubscriptionType.map(key => (
                  <SelectOption value={key} key={key}>
                    {capitalize(key.replaceAll('_', ' ').toLowerCase())}
                  </SelectOption>
                ))}
              </Select>
            </Form.Item>
          </Row>
          <Row>
            <Form.Item
              style={{ minWidth: '100%' }}
              label="Sub-Type"
              validateStatus={
                !userSubRoleValid &&
                fieldsAreDirty.subRole &&
                supraSubscriptionType.length !== 0
                  ? 'error'
                  : undefined
              }
              help={subRoleHelpMessage}
            >
              <Select
                data-cy="user-select-supra-sub"
                showSearch
                placeholder="Select"
                disabled={selectedKeyType === SupraSystemSubscriptionType.FULL}
                optionFilterProp="children"
                value={selectedSubRole}
                onChange={handleSubKeyTypeChange}
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
                onBlur={() =>
                  setFieldsAreDirty(cur => ({
                    ...cur,
                    keyType: true,
                    subRole: true,
                  }))
                }
                style={{ textTransform: 'capitalize' }}
              >
                {supraSubKeyTypes?.map(type => (
                  <SelectOption value={type} key={type}>
                    {capitalize(type.replaceAll('_', ' ').toLowerCase())}
                  </SelectOption>
                ))}
              </Select>
            </Form.Item>
          </Row>
          {billableStatus === 'billable' && canWaiveActivationFee && (
            <Paragraph>
              <StyledCheckbox
                checked={waiveFee}
                disabled={isActivationFeeAutomaticallyWaived}
                onChange={e => handleWaiveActivationFee(e.target.checked)}
              >
                Waive Activation Fee&nbsp;
              </StyledCheckbox>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip-id">
                    <div style={{ textAlign: 'left' }}>
                      {isActivationFeeAutomaticallyWaived
                        ? `Legacy eKey users always have their activation fee waived on their first Supra One subscription.`
                        : `The agent will not need to pay the ${activationRate} one-time activation fee for Supra services.`}
                    </div>
                  </Tooltip>
                }
              >
                <Inline>
                  <Icon type="antdInfoCircleOutlined" color="black" />
                </Inline>
              </OverlayTrigger>
            </Paragraph>
          )}
        </>
      )}

      {featureEnabled('keyholderBillingFeature') &&
        waiveFee &&
        billableStatus === 'billable' &&
        !isActivationFeeAutomaticallyWaived && (
          <Paragraph>
            <label htmlFor="waive-reason">Reason</label>
            <TextArea
              id="waive-reason"
              placeholder="500 characters"
              value={subscribeWithoutFeeReason}
              onChange={e =>
                setSubscribeWithoutFeeReason(e.target.value.slice(0, 500))
              }
              maxLength={500}
              rows={3}
            />
          </Paragraph>
        )}

      {isTrainingKeyLimitExceeded && (
        <Paragraph>
          <ErrorText>
            You have issued the maximum number of training keys per your
            contract. Please unassign any training keys that may no longer be in
            use and try again.
          </ErrorText>
        </Paragraph>
      )}

      <Paragraph>
        <ActionsWrapper>
          <Space>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              type="primary"
              loading={loading}
              onClick={onSendInvite}
              disabled={isTrainingKeyLimitExceeded}
            >
              Send Invite
            </Button>
          </Space>
        </ActionsWrapper>
      </Paragraph>
    </Modal>
  );
};

const StyledCheckbox = styled(Checkbox)`
  margin-top: 8px;
`;

const Inline = styled.div`
  display: inline-block;
  margin-left: 8px;
`;

// Styled component to replace the inline styles
const ActionsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const StyledTitle = styled(Title)`
  /* !important to override antd default margin of 0.5em */
  margin-bottom: 24px !important;
`;

const ErrorText = styled.span`
  color: #ff4d4f;
  display: block;
`;

export default SubscribeUserModal;
